import FormFieldCalendar from '@/components/FormFieldCalendar';
import FormFieldComponent from '@/components/FormFieldComponent';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PaymentType, WorkOrderPaymentStatus } from '@/types/workOrder.types';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

export const PaymentStatusBadge: Record<
  WorkOrderPaymentStatus,
  {
    label: string;
    bgColor: string;
    fontColor: string;
  }
> = {
  CASHED: {
    bgColor: '#D5F4E3',
    fontColor: '#1B8652',
    label: 'Cashed',
  },
  BILLED: {
    label: 'Issued',
    bgColor: '#E1EAFB',
    fontColor: '#1551B2',
  },
  REQUESTED: {
    label: 'Requested',
    bgColor: '#FBEDFF',
    fontColor: '#551175',
  },
  VOID: {
    label: 'Void',
    bgColor: '#CAC8C8',
    fontColor: '#676767',
  },
};

export const WorkOrderPaymentForm = ({
  name,
  disabled,
  header,
  onRemove,
  paymentType,
}: {
  name: string;
  disabled: boolean;
  header: string;
  onRemove?: () => void;
  paymentType: PaymentType;
}) => {
  const { control } = useFormContext();
  const [openPaymentInfo, setOpenPaymentInfo] = useState(false);

  return (
    <Collapsible className="flex flex-col bg-[#F5F5F5] w-full px-3 rounded-md cursor-pointer">
      <div className="flex flex-row justify-between items-center">
        <CollapsibleTrigger
          className="flex flex-row justify-between p-3 w-full"
          onClick={() => setOpenPaymentInfo(!openPaymentInfo)}
        >
          <div className="flex flex-row gap-1">
            <span className="text-base font-[600]">{header}</span>
          </div>
        </CollapsibleTrigger>
        <div className="flex flex-row gap-2 items-center">
          {!disabled && onRemove && (
            <Trash2
              className="w-4 h-4 text-error cursor-pointer hover:opacity-80"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            />
          )}
          <CollapsibleTrigger onClick={() => setOpenPaymentInfo(!openPaymentInfo)}>
            {openPaymentInfo ? <ChevronUp /> : <ChevronDown />}
          </CollapsibleTrigger>
        </div>
      </div>
      {openPaymentInfo && (
        <div className="flex flex-col bg-[#F5F5F5] gap-4 px-3 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormFieldCalendar
              control={control}
              name={`${name}.issueDate`}
              label="Date"
              placeholder="Select Date"
              required
              disabled={disabled}
            />
            <FormFieldComponent
              control={control}
              name={`${name}.reference`}
              label="Reference"
              placeholder="Enter Reference"
              disabled={disabled}
            />
          </div>
          <div className="grid grid-cols-1 gap-6">
            <FormFieldComponent
              control={control}
              name={`${name}.issuedTo`}
              label="Issued To"
              placeholder="Enter Issued To"
              required
              disabled={disabled}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormFieldComponent
              control={control}
              name={`${name}.checkNumber`}
              label="Check #"
              placeholder="Enter Check Number"
              required={paymentType === PaymentType.WITNESS_FEE}
              type="number"
              allowDecimals={false}
              disabled={disabled}
            />
            <FormFieldComponent
              control={control}
              name={`${name}.amount`}
              label="Amount"
              placeholder="Enter Amount"
              type="number"
              required
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </Collapsible>
  );
};
